import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatNumberWithSpaces, formatCurrency } from 'utils/stringUtils';
import { BRAND_PRIMARY_RGB, addBrandLogo } from 'views/Admin/pdf/pdfBranding';
import { CalculatorSettings, RoiFilters, RoiInsightsData } from '../types';
import { PeriodSelection } from 'views/Admin/UsageReport/types';

const periodLabel = (period: PeriodSelection): string => {
  if (period.kind === 'preset') return period.value.toLowerCase().replace(/_/g, '-');
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return `custom-${fmt(period.from)}_${fmt(period.to)}`;
};

const buildFilename = (period: PeriodSelection): string => {
  const today = new Date().toISOString().slice(0, 10);
  return `roi-insights_${periodLabel(period)}_${today}.pdf`;
};

const workspaceLabelFor = (filters: RoiFilters): string => {
  if (filters.workspaceIds.length === 0) return 'All workspaces';
  if (filters.workspaceIds.length === 1) return `1 workspace`;
  return `${filters.workspaceIds.length} workspaces`;
};

const lastY = (doc: jsPDF): number => ((doc as unknown) as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

export const exportRoiInsightsPdf = (
  data: RoiInsightsData,
  settings: CalculatorSettings,
  filters: RoiFilters,
): void => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  // Header — BlazeMeter logo top-right, kept clear of the period line below.
  addBrandLogo(doc);

  doc.setFontSize(16);
  doc.text('ROI Insights', 40, 40);
  doc.setFontSize(10);
  doc.text(
    `Period: ${periodLabel(filters.period)} · ${workspaceLabelFor(filters)} · Generated: ${new Date().toISOString()}`,
    40,
    58,
  );

  // === Cost avoidance calculator section ===
  doc.setFontSize(12);
  doc.text('Cost avoidance calculator', 40, 80);

  // 1. Inputs
  autoTable(doc, {
    startY: 90,
    head: [['Setting', 'Value']],
    body: [
      ['Cost per live API call', `$${settings.costPerCall.toFixed(2)}`],
      ['Hit rate', `${Math.round(data.summary.effectiveHitRate * 100)}%`],
    ],
    styles: { fontSize: 8 },
    headStyles: { fillColor: BRAND_PRIMARY_RGB },
    theme: 'striped',
  });

  // 2. KPI cards
  autoTable(doc, {
    head: [
      [
        'Savings this period',
        'Projected annual savings',
        'Total virtual services requests',
        'Hits served by virtual service',
        'Account hit rate',
        'Active services',
      ],
    ],
    body: [
      [
        formatCurrency(data.summary.estimatedCostAvoided),
        formatCurrency(data.summary.projectedAnnualSavings),
        formatNumberWithSpaces(data.summary.totalRequests),
        formatNumberWithSpaces(data.summary.hitsServedByVs),
        `${Math.round(data.summary.effectiveHitRate * 100)}%`,
        `${data.summary.activeServiceCount} of ${data.summary.totalServiceCount}`,
      ],
    ],
    styles: { fontSize: 8 },
    headStyles: { fillColor: BRAND_PRIMARY_RGB },
    theme: 'striped',
  });

  // === Statistics section ===
  doc.setFontSize(12);
  // Track the current baseline manually: doc.text does not advance lastAutoTable.finalY,
  // so the first sub-section title must be offset from the "Statistics" header (not from
  // the previous table) to avoid drawing both labels at the same Y position.
  let cursorY = lastY(doc) + 24;
  doc.text('Statistics', 40, cursorY);

  // Helper for 6 statistics sub-sections (label + table). When a section has no rows we
  // skip the table entirely and render an explanatory caption instead of an empty grid.
  const addStatsTable = (title: string, head: string[][], body: (string | number)[][]) => {
    doc.setFontSize(10);
    const titleY = cursorY + 24;
    doc.text(title, 40, titleY);
    if (body.length === 0) {
      doc.setFontSize(8);
      const captionY = titleY + 16;
      doc.text('No data available for the selected period.', 40, captionY);
      cursorY = captionY;
      return;
    }
    autoTable(doc, {
      startY: titleY + 8,
      head,
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: BRAND_PRIMARY_RGB },
      theme: 'striped',
    });
    cursorY = lastY(doc);
  };

  // 3. Top 8 virtual services by request volume
  addStatsTable(
    'Top 8 virtual services by request volume',
    [['Service name', 'Total requests']],
    data.topByVolume.map(s => [s.serviceName, formatNumberWithSpaces(s.total)]),
  );

  // 4. Hit rate by virtual service (top active)
  addStatsTable(
    'Hit rate by virtual service (top active)',
    [['Service name', 'Hit rate']],
    data.topByHitRate.map(s => [s.serviceName, `${Math.round(s.hitRate * 100)}%`]),
  );

  // 5. Hit vs. miss breakdown
  addStatsTable(
    'Hit vs. miss breakdown — top 8 virtual services',
    [['Service name', 'Hits', 'Misses']],
    data.hitMissBreakdown.map(s => [s.serviceName, formatNumberWithSpaces(s.hits), formatNumberWithSpaces(s.misses)]),
  );

  // 6. Requests by workspace
  addStatsTable(
    'Requests by workspace',
    [['Workspace', 'Total requests']],
    data.workspaceRequests.map(w => [w.workspaceName, formatNumberWithSpaces(w.totalRequests)]),
  );

  // 7. Idle virtual services
  addStatsTable(
    'Idle virtual services — candidates for cleanup',
    [['Service name', 'Idle since']],
    data.idleCandidates.map(e => [e.serviceName, e.idleSinceLabel]),
  );

  // 8. Location utilisation
  addStatsTable(
    'Location utilisation',
    [['Location', 'Requests']],
    data.locationUtilisation.map(l => [
      l.locationName,
      l.totalRequests > 0 ? `${formatNumberWithSpaces(l.totalRequests)} reqs` : 'No activity',
    ]),
  );

  doc.save(buildFilename(filters.period));
};
